import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight, CheckCircle, BarChart } from 'lucide-react';
type CaseStudy = {
  client: string;
  title: string;
  metric: string;
  metricLabel: string;
  image: string;
  tags: string[];
};
type Props = {
  isOpen: boolean;
  onClose: () => void;
  data: CaseStudy | null;
};
export function CaseStudyModal({
  isOpen,
  onClose,
  data
}: Props) {
  if (!data) return null;
  return <AnimatePresence data-id="element-91">
      {isOpen && <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 md:p-8" data-id="element-92">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="absolute inset-0 bg-[#0a1628]/90 backdrop-blur-sm" data-id="element-93" />

          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} className="relative bg-[#1a2d4a] w-full max-w-4xl max-h-[90vh] overflow-y-auto border border-white/10 shadow-2xl flex flex-col md:flex-row" data-id="element-94">
            <button onClick={onClose} className="absolute top-4 right-4 z-10 bg-[#0a1628]/50 p-2 rounded-full text-white hover:bg-[#d4a853] hover:text-[#0a1628] transition-colors" data-id="element-95">
              <X size={24} data-id="element-96" />
            </button>

            {/* Image Side */}
            <div className="w-full md:w-2/5 relative h-64 md:h-auto" data-id="element-97">
              <img src={data.image} alt={data.title} className="w-full h-full object-cover" data-id="element-98" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1a2d4a] to-transparent md:bg-gradient-to-r" data-id="element-99" />

              <div className="absolute bottom-6 left-6 right-6" data-id="element-100">
                <div className="flex flex-wrap gap-2 mb-4" data-id="element-101">
                  {data.tags.map((tag, i) => <span key={i} className="bg-[#d4a853] text-[#0a1628] px-3 py-1 text-xs font-bold uppercase tracking-wider" data-id="element-102">
                      {tag}
                    </span>)}
                </div>
                <div className="flex items-baseline gap-2" data-id="element-103">
                  <span className="text-5xl font-bold text-white" data-id="element-104">
                    {data.metric}
                  </span>
                  <span className="text-[#d4a853] font-medium uppercase tracking-wider text-sm" data-id="element-105">
                    {data.metricLabel}
                  </span>
                </div>
              </div>
            </div>

            {/* Content Side */}
            <div className="w-full md:w-3/5 p-8 md:p-12 bg-[#0a1628]" data-id="element-106">
              <h4 className="text-[#d4a853] font-bold uppercase tracking-wider text-sm mb-2" data-id="element-107">
                Case Study
              </h4>
              <h2 className="text-3xl font-bold text-white mb-2" data-id="element-108">
                {data.title}
              </h2>
              <p className="text-gray-400 mb-8 text-lg" data-id="element-109">{data.client}</p>

              <div className="space-y-8" data-id="element-110">
                <div data-id="element-111">
                  <h3 className="text-white font-bold text-lg mb-3 flex items-center gap-2" data-id="element-112">
                    <div className="w-1 h-6 bg-[#d4a853]" data-id="element-113" />
                    The Challenge
                  </h3>
                  <p className="text-gray-400 leading-relaxed" data-id="element-114">
                    The client faced significant delays in their supply chain
                    due to regulatory hurdles and inefficient logistics
                    partners. Costs were spiraling, and delivery times were
                    unpredictable, affecting their market reputation.
                  </p>
                </div>

                <div data-id="element-115">
                  <h3 className="text-white font-bold text-lg mb-3 flex items-center gap-2" data-id="element-116">
                    <div className="w-1 h-6 bg-[#d4a853]" data-id="element-117" />
                    Our Solution
                  </h3>
                  <p className="text-gray-400 leading-relaxed" data-id="element-118">
                    We conducted a comprehensive audit of their logistics
                    network and implemented a diversified carrier strategy. We
                    also renegotiated contracts and established a centralized
                    compliance team to handle customs documentation.
                  </p>
                </div>

                <div data-id="element-119">
                  <h3 className="text-white font-bold text-lg mb-3 flex items-center gap-2" data-id="element-120">
                    <div className="w-1 h-6 bg-[#d4a853]" data-id="element-121" />
                    Key Results
                  </h3>
                  <ul className="space-y-3" data-id="element-122">
                    <li className="flex items-start gap-3 text-gray-300" data-id="element-123">
                      <CheckCircle className="w-5 h-5 text-[#d4a853] flex-shrink-0 mt-0.5" data-id="element-124" />
                      <span data-id="element-125">Reduced average transit time by 14 days</span>
                    </li>
                    <li className="flex items-start gap-3 text-gray-300" data-id="element-126">
                      <CheckCircle className="w-5 h-5 text-[#d4a853] flex-shrink-0 mt-0.5" data-id="element-127" />
                      <span data-id="element-128">Achieved 99.8% customs compliance rate</span>
                    </li>
                    <li className="flex items-start gap-3 text-gray-300" data-id="element-129">
                      <CheckCircle className="w-5 h-5 text-[#d4a853] flex-shrink-0 mt-0.5" data-id="element-130" />
                      <span data-id="element-131">Lowered overall logistics spend by 22%</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="mt-12 pt-8 border-t border-white/10" data-id="element-132">
                <button className="flex items-center gap-2 text-[#d4a853] font-bold uppercase tracking-wider hover:gap-4 transition-all" data-id="element-133">
                  Download Full Report <ArrowRight size={16} data-id="element-134" />
                </button>
              </div>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}